import { useEffect, useState } from "react";
import { useAppDispatch, useAppSelector } from "../../hooks";
import { addAlert } from "../../slices/alert";
import {
  updatePasswordByRecovery,
  verifyPasswordRecoverySession,
} from "../../slices/auth";
import { generateTranslatedText } from "../../utils/boilerplate";
import { ArrowUturnLeftIcon, LifebuoyIcon, PaperAirplaneIcon } from "@heroicons/react/24/solid";
import { useNavigate, useSearchParams } from "react-router-dom";
import Card from "../layout/Card";
import SpaceFiller from "../layout/SpaceFiller";
import ClickableLink from "../layout/ClickableLink";
import Button from "../layout/Button";
import TextInput from "../forms/TextInput";
import { ArrowLongDownIcon } from "@heroicons/react/20/solid";

export default function ResetPasswordForm() {
  const dispatch = useAppDispatch();

  // App level state
  const language = "EN";

  const [searchParams] = useSearchParams();
  const navigate = useNavigate();

  // Get query parameters
  const profileId = searchParams.get("profile");
  const sessionId = searchParams.get("session");

  useEffect(() => {
    dispatch(verifyPasswordRecoverySession({ profileId, sessionId }));
  }, [dispatch, profileId, sessionId]);

  // Get app level state
  const {
    email: {
      recovery: { loading, verified },
    },
  } = useAppSelector((state) => state.auth);

  // Component level state
  const [state, setState] = useState({
    password: "",
    confirm_password: "",
  });

  // Functions
  async function submitNewPassword(e: any) {
    e.preventDefault();

    const { password, confirm_password: confirmPassword } = state;

    // Validate length
    if (password === "") {
      dispatch(
        addAlert(
          generateTranslatedText("missing_new_password", language),
          "orange"
        )
      );
      return;
    }
    if (confirmPassword === "") {
      dispatch(
        addAlert(
          generateTranslatedText("missing_confirm_password", language),
          "orange"
        )
      );
      return;
    }

    // Validate if passwords match
    if (password !== confirmPassword) {
      dispatch(
        addAlert(
          generateTranslatedText("password_mismatch", language),
          "orange"
        )
      );
      return;
    }

    // Submit passwords for update
    const res: any = await dispatch(
      updatePasswordByRecovery({
        profileId,
        password,
        confirmPassword,
        sessionId,
      })
    );
    if (res.payload.status === 200) {
      dispatch(
        addAlert(
          generateTranslatedText("password_recovery_success", language),
          "mint-green"
        )
      );
      navigate("/");
      return;
    }
    if (res.payload.message !== undefined) {
      dispatch(
        addAlert(
          generateTranslatedText(res.payload.message, language),
          "orange"
        )
      );
      return;
    }
    dispatch(
      addAlert(
        generateTranslatedText("password_recovery_error", language),
        "orange"
      )
    );
  }

  return (
    <div className="flex min-h-full items-center justify-center py-12 px-4 sm:px-6 lg:px-8">
      <SpaceFiller>
        {loading ? (
          <div className="flex justify-center items-center">
            {/* <LoadingBubbles /> */}
          </div>
        ) : !verified ? (
          <>
            <Card
              colour="white"
              borderColour="none"
              paddingX="px-8"
              paddingY="py-8"
              className=" w-full max-w-md space-y-8"
            >
              {/* Header */}
              <div>
                <a href="/">
                  <img
                    className="mx-auto h-12 w-auto rounded-lg"
                    src="/img/logo/logo.svg"
                    alt="RaiseDiversity"
                  />
                </a>
                <h2 className="mt-6 text-center text-3xl font-bold tracking-tight text-black">
                  {generateTranslatedText("link_expired", language)}
                </h2>
                <p className="mt-5 text-secondary text-center text-sm">
                  {generateTranslatedText("link_expired_subtext", language)}
                </p>
              </div>

              {/* Form */}
              <div className="mt-8 space-y-6">
                <Button
                  type="submit"
                  bgColour="dark-blue"
                  textColour="white"
                  borderColour="dark-blue"
                  onClick={() => window.location.href = "/forgot-my-password"}
                >
                  <span className="absolute inset-y-0 left-0 flex items-center pl-3">
                    <ArrowUturnLeftIcon
                      className="h-5 w-5 text-white"
                      aria-hidden="true"
                    />
                  </span>
                  {generateTranslatedText("start_again", language)}
                </Button>
              </div>
            </Card>
          </>
        ) : (
          <>
            {/* Card */}
            <Card
              colour="white"
              paddingX="px-8"
              paddingY="py-8"
              borderColour="none"
              className=" w-full max-w-md space-y-8"
            >
              {/* Header */}
              <div>
                <a href="/">
                  <img
                    className="mx-auto h-12 w-auto rounded-lg"
                    src="/img/logo/logo.svg"
                    alt="Raisediversity"
                  />
                </a>
                <h2 className="mt-6 text-center text-3xl font-bold tracking-tight text-black">
                  {generateTranslatedText("reset_password", language)}
                </h2>
                <p className="mt-5 text-secondary text-center text-sm">
                  {generateTranslatedText("reset_password_subtext", language)}
                </p>
              </div>

              {/* Form */}
              <form
                className="mt-8 space-y-4 grid grid-cols-1 gap-4"
                onSubmit={(e) => submitNewPassword(e)}
              >
                {/* New password */}
                <TextInput
                  id="new_password"
                  type="password"
                  label={generateTranslatedText("new_password", language)}
                  labelColour="text-white"
                  required={true}
                  showLabel={true}
                  placeholder={generateTranslatedText(
                    "new_password_placeholder",
                    language
                  )}
                  onChange={(e) => setState({ ...state, password: e.target.value })}
                  bgColour="black-light"
                  textColour="black"
                  borderClass="border-1 border-light/50"
                  placeholderColour="light/50"
                  value={state.password}
                />

                {/* Confirm password */}
                <TextInput
                  id="confirm_password"
                  type="password"
                  label={generateTranslatedText("confirm_password", language)}
                  labelColour="text-white"
                  required={true}
                  showLabel={true}
                  placeholder={generateTranslatedText(
                    "confirm_password_placeholder",
                    language
                  )}
                  onChange={(e) => setState({ ...state, confirm_password: e.target.value })}
                  bgColour="black-light"
                  textColour="black"
                  borderClass="border-1 border-light/50"
                  placeholderColour="light/50"
                  value={state.confirm_password}
                />

                <Button
                  type="submit"
                  bgColour="dark-blue"
                  textColour="white"
                  borderColour="dark-blue"
                >
                  <span className="absolute inset-y-0 left-0 flex items-center pl-3">
                    <LifebuoyIcon
                      className="h-5 w-5 text-white"
                      aria-hidden="true"
                    />
                  </span>
                  {generateTranslatedText("submit", language)}
                </Button>
              </form>
            </Card>
          </>
        )}
      </SpaceFiller>
    </div>
  );
}
